import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import api from '../utils/api';
import { useNotification } from '../components/Notification';
import { GoldenAmount, OrderStatusBadge } from '../components/store/StoreUi';

const STEPS = ['placed', 'processing', 'packed', 'shipped', 'delivered'];

export default function StoreOrderDetail() {
  const { id } = useParams();
  const location = useLocation();
  const { showNotification } = useNotification();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/store/orders/${id}`);
      setOrder(data.order);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.message || 'Order not found.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const cancelOrder = async () => {
    if (!window.confirm('Cancel this order and refund the golden tickets?')) return;
    setCancelling(true);
    try {
      const { data } = await api.post(`/store/orders/${id}/cancel`, { reason: 'Cancelled by customer' });
      setOrder(data.order);
      showNotification('Order cancelled and golden tickets refunded.', 'success');
    } catch (err) {
      showNotification(err?.response?.data?.message || 'Order could not be cancelled.', 'error');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 dark:bg-[#090d16] p-12"><div className="mx-auto h-96 max-w-5xl animate-pulse rounded-3xl bg-slate-200 dark:bg-slate-800" /></div>;
  }
  if (error || !order) {
    return <div className="min-h-screen bg-slate-50 dark:bg-[#090d16] px-4 py-20 text-center"><h1 className="text-2xl font-black dark:text-white">{error}</h1><Link to="/store/orders" className="mt-5 inline-flex text-amber-600 underline">Back to orders</Link></div>;
  }

  const activeIndex = order.status === 'cancelled' ? -1 : STEPS.indexOf(order.status);

  return (
    <main className="min-h-screen bg-slate-50 dark:bg-[#090d16] py-8 sm:py-10">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {location.state?.justPlaced && (
          <div className="mb-6 rounded-2xl border border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/20 p-5">
            <p className="text-lg font-black text-emerald-800 dark:text-emerald-300">Order confirmed 🎉</p>
            <p className="mt-1 text-sm text-emerald-700 dark:text-emerald-400">Your golden tickets were deducted and your order is now with the store team.</p>
          </div>
        )}

        <div className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Link to="/store/orders" className="text-sm font-semibold text-amber-700 dark:text-amber-400 hover:underline">← My orders</Link>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <h1 className="text-3xl sm:text-4xl font-black text-slate-950 dark:text-white">{order.orderNumber}</h1>
              <OrderStatusBadge status={order.status} />
            </div>
            <p className="mt-1 text-sm text-slate-500">Placed {new Date(order.createdAt).toLocaleString()}</p>
          </div>
          {['placed', 'processing'].includes(order.status) && (
            <button
              onClick={cancelOrder}
              disabled={cancelling}
              className="rounded-xl border border-red-200 dark:border-red-800 px-4 py-2.5 text-sm font-bold text-red-600 dark:text-red-400 disabled:opacity-50"
            >
              {cancelling ? 'Cancelling…' : 'Cancel order'}
            </button>
          )}
        </div>

        {order.status !== 'cancelled' ? (
          <section className="mb-6 rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 p-5 sm:p-7">
            <h2 className="font-black text-slate-900 dark:text-white">Fulfillment progress</h2>
            <div className="mt-6 grid grid-cols-5">
              {STEPS.map((step, index) => {
                const complete = index <= activeIndex;
                return (
                  <div key={step} className="relative text-center">
                    {index > 0 && <div className={`absolute right-1/2 top-4 h-1 w-full ${index <= activeIndex ? 'bg-amber-400' : 'bg-slate-200 dark:bg-slate-700'}`} />}
                    <span className={`relative z-10 mx-auto flex h-9 w-9 items-center justify-center rounded-full text-sm font-black ${complete ? 'bg-amber-400 text-slate-950' : 'bg-slate-200 dark:bg-slate-700 text-slate-500'}`}>
                      {complete ? '✓' : index + 1}
                    </span>
                    <p className="mt-2 text-[10px] sm:text-xs font-bold capitalize text-slate-600 dark:text-slate-300">{step}</p>
                  </div>
                );
              })}
            </div>
          </section>
        ) : (
          <section className="mb-6 rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 p-5">
            <h2 className="font-black text-red-800 dark:text-red-300">Order cancelled</h2>
            <p className="mt-1 text-sm text-red-700 dark:text-red-400">{order.cancellationReason} Your {order.totalGolden} golden tickets were refunded.</p>
          </section>
        )}

        {order.status === 'shipped' || order.status === 'delivered' ? (
          <section className="mb-6 rounded-2xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20 p-5 sm:p-6">
            <h2 className="font-black text-slate-900 dark:text-white">Shipping & tracking</h2>
            <div className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
              <p><span className="text-slate-500">Carrier:</span> <strong className="dark:text-white">{order.tracking?.carrier || 'Not specified'}</strong></p>
              <p><span className="text-slate-500">Tracking:</span> <strong className="dark:text-white">{order.tracking?.trackingNumber || 'Pending'}</strong></p>
            </div>
            {order.tracking?.trackingUrl && (
              <a href={order.tracking.trackingUrl} target="_blank" rel="noreferrer" className="mt-4 inline-flex rounded-xl bg-slate-950 dark:bg-white px-4 py-2.5 text-sm font-bold text-white dark:text-slate-950">
                Track shipment ↗
              </a>
            )}
          </section>
        ) : null}

        <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
          <section className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900">
            <h2 className="border-b border-slate-100 dark:border-white/5 px-5 py-4 font-black text-slate-900 dark:text-white">Order items</h2>
            <div className="divide-y divide-slate-100 dark:divide-white/5">
              {order.items.map((item, index) => (
                <div key={`${item.product}-${index}`} className="flex gap-4 p-4 sm:p-5">
                  <Link to={`/store/product/${item.productSlug}`} className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-slate-100 dark:bg-slate-800">
                    {item.image ? <img src={item.image} alt={item.productName} className="h-full w-full object-cover" /> : <span className="flex h-full items-center justify-center">🎁</span>}
                  </Link>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-slate-900 dark:text-white">{item.productName}</p>
                    {item.variantName && <p className="text-xs text-slate-500">{item.variantName}</p>}
                    <p className="mt-2 text-sm text-slate-500">Quantity {item.quantity}</p>
                  </div>
                  <GoldenAmount amount={item.lineTotalGolden} />
                </div>
              ))}
            </div>
          </section>

          <aside className="space-y-5">
            <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 p-5">
              <h2 className="font-black text-slate-900 dark:text-white">Payment</h2>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-sm text-slate-500">Total paid</span>
                <GoldenAmount amount={order.totalGolden} />
              </div>
              <p className={`mt-2 text-xs font-bold capitalize ${order.paymentStatus === 'refunded' ? 'text-red-600' : 'text-emerald-600'}`}>{order.paymentStatus}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 p-5">
              <h2 className="font-black text-slate-900 dark:text-white">Delivery address</h2>
              <address className="mt-3 not-italic text-sm leading-6 text-slate-600 dark:text-slate-300">
                <strong className="text-slate-900 dark:text-white">{order.shippingAddress.fullName}</strong><br />
                {order.shippingAddress.addressLine1}<br />
                {order.shippingAddress.addressLine2 && <>{order.shippingAddress.addressLine2}<br /></>}
                {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postalCode}<br />
                {order.shippingAddress.country}<br />
                {order.shippingAddress.phone}
              </address>
            </div>
          </aside>
        </div>

        {order.timeline?.length > 0 && (
          <section className="mt-6 rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 p-5 sm:p-6">
            <h2 className="font-black text-slate-900 dark:text-white">Order timeline</h2>
            <ol className="mt-4 space-y-4">
              {[...order.timeline].reverse().map((event, index) => (
                <li key={`${event.at}-${index}`} className="flex gap-3">
                  <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-amber-400" />
                  <div>
                    <p className="text-sm font-bold capitalize text-slate-800 dark:text-slate-200">{event.status}</p>
                    <p className="text-xs text-slate-500">{event.note}</p>
                    <time className="text-[11px] text-slate-400">{new Date(event.at).toLocaleString()}</time>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>
    </main>
  );
}
